/**
 * Chat Messages Component
 * Displays chat messages in a scrollable container
 */

import React, { useState, useEffect, useRef } from 'react';
import './ChatMessages.css';
import { format } from 'date-fns';
import { User, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const ChatMessages = ({ messages, loading, onSendToSensei, scenarioContext }) => {
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      
      if (text.length > 0 && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const container = messagesContainerRef.current;
        
        // Check if selection is within the chat messages container
        if (container && container.contains(range.commonAncestorContainer)) {
          setSelectedText(text);
          
          // Find which message contains this selection
          let foundMessage = null;
          for (const message of messages) {
            const messageElement = container.querySelector(`[data-message-id="${message.id}"]`);
            if (messageElement) {
              // Check if the selection range intersects with this message element
              const messageRect = messageElement.getBoundingClientRect();
              const selectionRect = range.getBoundingClientRect();
              
              // Check if selection overlaps with message
              if (selectionRect.top < messageRect.bottom && 
                  selectionRect.bottom > messageRect.top &&
                  selectionRect.left < messageRect.right &&
                  selectionRect.right > messageRect.left) {
                foundMessage = message;
                break;
              }
            }
          }
          setSelectedMessage(foundMessage);
          
          // Get position of selection for floating button
          const rect = range.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          
          // Calculate position relative to container, accounting for scroll
          setSelectionPosition({
            top: rect.bottom - containerRect.top + scrollTop + 10,
            left: rect.left - containerRect.left + (rect.width / 2)
          });
        } else {
          // Selection is outside chat messages, clear it
          setSelectedText('');
          setSelectionPosition(null);
          setSelectedMessage(null);
        }
      } else {
        setSelectedText('');
        setSelectionPosition(null);
        setSelectedMessage(null);
      }
    };

    const handleMouseUp = () => {
      // Small delay to allow selection to complete
      setTimeout(handleSelection, 10);
    };

    const handleClick = (e) => {
      // Don't clear if clicking the button itself
      if (e.target.closest('.send-to-sensei-button')) {
        return;
      }
      
      // Small delay to allow selection to clear if clicking elsewhere
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection.toString().trim().length === 0) {
          setSelectedText('');
          setSelectionPosition(null);
          setSelectedMessage(null);
        }
      }, 100);
    };

    document.addEventListener('selectionchange', handleSelection);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelection);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('click', handleClick);
    };
  }, [messages]);

  // Auto-scroll to bottom when messages change or when component mounts
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (messagesContainerRef.current) {
        // Fallback: scroll the container itself
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages, loading]);

  // Also scroll immediately when messages array length changes (new message added)
  useEffect(() => {
    if (messages.length > 0) {
      const scrollToBottom = () => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      };
      
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        setTimeout(scrollToBottom, 50);
      });
    }
  }, [messages.length]);

  const handleSendToSensei = () => {
    if (selectedText && onSendToSensei) {
      onSendToSensei(selectedText, selectedMessage, scenarioContext);
      setSelectedText('');
      setSelectionPosition(null);
      setSelectedMessage(null);
      window.getSelection().removeAllRanges();
    }
  };

  return (
    <div className="chat-messages" ref={messagesContainerRef} style={{ position: 'relative' }}>
      {messages.length === 0 && (
        <div className="empty-state">
          <p>No messages yet. Start a conversation!</p>
        </div>
      )}
      
      {messages.map(message => (
        <div
          key={message.id}
          data-message-id={message.id}
          className={`message ${message.type === 'user' ? 'user-message' : 'stakeholder-message'}`}
        >
          <div className="message-avatar">
            <User size={20} />
          </div>
          
          <div className="message-content">
            {message.type === 'stakeholder' && message.stakeholder && (
              <div className="message-header">
                <span className="message-author">{message.stakeholder.name}</span>
                <span className="message-role">{message.stakeholder.role}</span>
                <span className="message-time">
                  {format(new Date(message.timestamp), 'HH:mm')}
                </span>
              </div>
            )}
            
            {message.type === 'user' && (
              <div className="message-header">
                <span className="message-author">You</span>
                <span className="message-time">
                  {format(new Date(message.timestamp), 'HH:mm')}
                </span>
              </div>
            )}
            
            <div className="message-text">
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>
                  ),
                }}
              >
                {message.text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      ))}
      
      {loading && (
        <div className="message loading-message">
          <div className="message-avatar">
            <User size={20} />
          </div>
          <div className="message-content">
            <div className="typing-indicator">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
      
      {selectedText && selectionPosition && (
        <button
          className="send-to-sensei-button"
          onClick={handleSendToSensei}
          style={{
            position: 'absolute',
            top: `${selectionPosition.top}px`,
            left: `${selectionPosition.left}px`,
            transform: 'translateX(-50%)'
          }}
          title={selectedMessage?.stakeholder ? `Ask Sensei about message from ${selectedMessage.stakeholder.name} (${selectedMessage.stakeholder.role})` : 'Ask Sensei about this message'}
        >
          <Send size={14} />
          Send to Sensei
        </button>
      )}
    </div>
  );
};

export default ChatMessages;







